import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { systemsAPI } from '../../services/api';
import { Panel, PanelTitle, Note } from './ui';
import { IST, plural, alpha } from './helpers';
import { statusColor } from '../../config/sensorRanges';

/*
 * Harvest: a two-card-wide panel at the top of the Dashboard's left column. Left: the first ripe
 * berry, the three figures and what the estimate rests on (pollen, what would move it). Right:
 * berries ripening per week, then each system's first ripe date and berries. The maths is
 * server/harvest_forecast.py (the farm assistant reads the same numbers); this only lays it out.
 * It is the one harvest date on the page: Growth no longer shows the stage curve's countdown.
 */

// Heavier than the Dashboard's 60 s sources and it moves only when a plant is checked, so it
// refreshes on its own slower clock (the route caches 5 min too).
const REFRESH_MS = 15 * 60 * 1000;
// The week strip always has at least this many slots, so three weeks of berries do not huddle
// at one end of a wide panel.
const MIN_WEEK_SLOTS = 6;

const dayMonth = ts => new Date((ts + IST) * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const kg = g => (g >= 1000 ? `${(g / 1000).toFixed(1)} kg` : `${Math.round(g)} g`);
const kgRange = (lo, hi) => (hi >= 1000 ? `${(lo / 1000).toFixed(1)}-${(hi / 1000).toFixed(1)} kg` : `${Math.round(lo)}-${Math.round(hi)} g`);
const CONFIDENCE = { high: ['normal', 'Confident'], medium: ['notice', 'Rough'], low: ['offline', 'Early guess'] };

const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  font-size: 12px; font-weight: 500; padding: 2px 8px; border-radius: 10px;
  color: ${p => p.theme.colors.textSecondary};
  background: ${p => alpha(statusColor(p.theme, p.$status), 0.12)};
  i { width: 6px; height: 6px; border-radius: 50%; background: ${p => statusColor(p.theme, p.$status)}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 32px;
  @media (max-width: 760px) { grid-template-columns: minmax(0, 1fr); gap: 24px; }
`;

const Summary = styled.div`
  display: flex; flex-direction: column; min-width: 0;
  .big { font-size: 32px; font-weight: 500; letter-spacing: -0.03em; line-height: 1.1; }
  .sub { margin-top: 4px; color: ${p => p.theme.colors.textSecondary}; }
  .sub b { font-weight: 500; color: ${p => p.theme.colors.text}; }
  .sub + .sub { margin-top: 2px; color: ${p => p.theme.colors.textMuted}; }
  .nw { white-space: nowrap; }
  .facts { margin-top: 16px; padding-top: 16px; border-top: 1px solid ${p => p.theme.colors.border};
    display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
  .facts b { display: block; font-size: 18px; font-weight: 500; white-space: nowrap; }
  .facts span { display: block; margin-top: 2px; font-size: 12px; color: ${p => p.theme.colors.textMuted}; white-space: nowrap; }
  .notes { margin-top: auto; padding-top: 20px; display: grid; gap: 8px; font-size: 13px; color: ${p => p.theme.colors.textSecondary}; }
  .notes p { margin: 0; }
  .notes b { font-weight: 500; color: ${p => p.theme.colors.text}; }
`;

const Weeks = styled.div`
  min-width: 0;
  .h { margin: 0 0 12px; font-size: 13px; font-weight: 500; color: ${p => p.theme.colors.textSecondary}; }
  .plot { display: grid; grid-template-columns: repeat(${p => p.$n}, minmax(0, 1fr)); gap: 8px; align-items: end; height: 112px;
    border-bottom: 1px solid ${p => p.theme.colors.border}; }
  .col { display: flex; flex-direction: column; justify-content: flex-end; align-items: center; height: 100%; min-width: 0; }
  .col .n { font-size: 12px; color: ${p => p.theme.colors.textSecondary}; margin-bottom: 4px; }
  .col i { display: block; width: 100%; max-width: 36px; border-radius: 4px 4px 0 0; }
  .axis { display: grid; grid-template-columns: repeat(${p => p.$n}, minmax(0, 1fr)); gap: 8px; margin-top: 6px;
    font-size: 11px; color: ${p => p.theme.colors.textMuted}; text-align: center; white-space: nowrap; }
  .racks-h { margin: 24px 0 8px; }
  .racks { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; font-size: 13px; }
  .racks li { display: grid; grid-template-columns: 96px 56px minmax(0, 1fr) 128px; align-items: center; gap: 12px; }
  .racks li > span:first-child { color: ${p => p.theme.colors.textSecondary}; }
  .racks .when { color: ${p => p.theme.colors.textMuted}; }
  .racks .bar { height: 6px; border-radius: 3px; background: ${p => p.theme.colors.border}; overflow: hidden; }
  .racks .bar i { display: block; height: 100%; border-radius: 3px; }
  .racks .val { text-align: right; color: ${p => p.theme.colors.textMuted}; white-space: nowrap; }
  @media (max-width: 560px) { .racks li { grid-template-columns: 84px 52px minmax(0, 1fr) auto; gap: 8px; } }
`;

export default function HarvestForecastCard({ order }) {
  const theme = useTheme();
  const [f, setF] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => systemsAPI.getHarvestForecast()
      .then(r => { if (!cancelled) { setF(r.data); setFailed(false); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const first = f?.first_ripe_berry;
  const total = f?.yield?.total || {};
  const overdue = f?.yield?.overdue || {};
  const [status, word] = CONFIDENCE[f?.confidence?.level] || CONFIDENCE.low;

  // Loading, failed, and a crop with nothing coming yet: the title and one plain sentence.
  if (!first || !total.berries) {
    const line = failed && !f ? 'Could not load the forecast. It retries every 15 minutes.'
      : !f ? 'Working out the forecast from every plant check'
        : overdue.berries > 0 ? `${plural(overdue.berries, 'berry', 'berries')} should be ripe now on ${plural(overdue.plants, 'plant')}. Log the harvest to start the next forecast.`
          : 'No buds on this crop yet. The forecast starts with the first bud on a plant check.';
    return (
      <Panel $order={order} aria-busy={!f && !failed}>
        <PanelTitle><span>Harvest</span></PanelTitle>
        <Note>{line}</Note>
      </Panel>
    );
  }

  const g = total.grams;
  const weeks = f.yield.weeks || [];
  // Slots by date from the first week, so a week with no berries is an empty slot, not skipped.
  const WEEK = 7 * 86400;
  const start = weeks[0]?.week_of;
  const span = weeks.length ? Math.round((weeks[weeks.length - 1].week_of - start) / WEEK) + 1 : 0;
  const slots = Math.max(MIN_WEEK_SLOTS, span);
  const byWeek = Object.fromEntries(weeks.map(w => [Math.round((w.week_of - start) / WEEK), w]));
  const max = Math.max(1, ...weeks.map(w => w.berries));
  const systems = f.yield.by_system || [];
  const sysMax = Math.max(1, ...systems.map(s => s.berries));
  const p = f.pollen || {};
  const rated = (p.flowering_plants || 0) - (p.unrated || 0);
  const weak = (p.sparse || 0) + (p.poor || 0);
  const levers = (f.levers || []).filter(l => l.kind !== 'rate_pollen').slice(0, 1);

  return (
    <Panel $order={order} aria-labelledby="harvest-title">
      <PanelTitle>
        <span id="harvest-title">Harvest</span>
        <Chip $status={status} title={(f.confidence?.reasons || []).join('\n')}><i />{word}</Chip>
      </PanelTitle>
      <Grid>
        <Summary>
          <div className="big">{dayMonth(first.likely)}</div>
          <div className="sub">
            First ripe berry, on <b>{first.system}</b> ({first.plant_id})
          </div>
          <div className="sub">Window <span className="nw">{dayMonth(first.earliest)}</span> to <span className="nw">{dayMonth(first.latest)}</span></div>
          <div className="facts">
            <div><b>{total.berries.toLocaleString('en-IN')}</b><span>berries coming</span></div>
            <div><b>{kg(g.likely)}</b><span>{kgRange(g.low, g.high)}</span></div>
            <div><b>{total.well_pct}%</b><span>well shaped</span></div>
          </div>
          <div className="notes">
            {overdue.berries > 0 && (
              <p><b>{overdue.berries}</b> more should be ripe now on {plural(overdue.plants, 'plant')}: check them or log the harvest</p>
            )}
            <p>
              {!p.flowering_plants ? 'No open flowers yet, so pollen is rated later'
                : !rated ? <>Pollen is not rated on any of the <b>{p.flowering_plants}</b> flowering plants, so they count as sparse. Rating it on the Plant Log firms up the shape figure</>
                  : <>Pollen rated on <b>{rated}</b> of {plural(p.flowering_plants, 'flowering plant')}{weak ? <>, <b>{weak}</b> sparse or poor</> : ''}</>}
            </p>
            {levers.map(l => <p key={l.kind}>{l.text}</p>)}
          </div>
        </Summary>

        <Weeks $n={slots}>
          <div className="h">Ripe berries per week</div>
          <div className="plot" role="img"
            aria-label={`Ripe berries per week: ${weeks.map(w => `week of ${dayMonth(w.week_of)}, ${w.berries}`).join('; ')}`}>
            {Array.from({ length: slots }, (_, i) => {
              const w = byWeek[i];
              return (
                <div className="col" key={i} title={w ? `Week of ${dayMonth(w.week_of)}: ${plural(w.berries, 'berry', 'berries')}, about ${kg(w.grams.likely)}` : undefined}>
                  {w && <span className="n">{w.berries}</span>}
                  {w && <i style={{ height: `${Math.max(3, (w.berries / max) * 80)}px`, background: theme.colors.chartBerry }} />}
                </div>
              );
            })}
          </div>
          <div className="axis" aria-hidden>
            {Array.from({ length: slots }, (_, i) => {
              const wk = start != null ? start + i * WEEK : null;
              return <span key={i}>{wk ? dayMonth(wk) : ''}</span>;
            })}
          </div>
          {systems.length > 0 && (
            <div className="h racks-h">By system: first ripe berry and berries coming</div>
          )}
          {systems.length > 0 && (
            <ul className="racks" aria-label="By system">
              {systems.map(s => (
                <li key={s.system_id}>
                  <span>{s.name}</span>
                  <span className="when">{dayMonth(s.first_ripe)}</span>
                  <span className="bar"><i style={{ width: `${(s.berries / sysMax) * 100}%`, background: theme.colors.textMuted }} /></span>
                  <span className="val">{s.berries} berries, {kg(s.grams.likely)}</span>
                </li>
              ))}
            </ul>
          )}
        </Weeks>
      </Grid>
    </Panel>
  );
}
